// Real API client for sniff-quest-api on quest.sniffary.ro/api

const API_BASE = '/api';

export const photoUrl = (key: string) => `${API_BASE}/photos/${key}`;

// ============================================================================
// Types (mirror what the worker returns)
// ============================================================================

export type ClaimStatus = 'pending' | 'confirmed' | 'expired' | 'cancelled';

export interface ApiDrop {
  id: string;
  quest_number: number;
  host_account: 'sniffary' | 'boopr' | 'mash';
  tier: 'small' | 'medium' | 'legendary';
  field_location_name: string;
  field_location_address: string | null;
  quest_date: string;
  quest_time_start: string | null;
  status: 'scheduled' | 'active' | 'closed' | 'completed';
  opened_at: number | null;
  closed_at: number | null;
  max_participants: number;
}

export interface ApiClaimSummary {
  position: number;
  dog_name: string;
  dog_breed: string;
  dog_photo_key: string;
  instagram_handle: string;
  status: ClaimStatus;
  claimed_at: number;
  confirmed_at: number | null;
}

export interface ApiActiveResponse {
  active: ApiDrop | null;
  top5?: ApiClaimSummary[];
  waitlist_count?: number;
  confirmed_count?: number;
  pending_count?: number;
  top5_open?: boolean;
}

export interface ClaimPayload {
  dog_name: string;
  dog_breed: string;
  dog_age_range: string;
  dog_size: string;
  dog_gender: string;
  dog_city: string;
  dog_superpower: string;
  dog_hobby: string;
  dog_photo_key: string;
  owner_name: string;
  instagram_handle: string;
  email: string;
  phone: string;
  consent_terms: boolean;
  consent_content_usage: boolean;
  consent_newsletter: boolean;
  lang: 'ro' | 'en';
}

export interface ClaimResponse {
  ok: true;
  position: number;
  on_waitlist: boolean;
  claim_id: string;
}

export interface ApiError {
  ok: false;
  error: string;
  status?: number;
}

// ============================================================================
// Calls
// ============================================================================

export async function fetchActiveDrop(): Promise<ApiActiveResponse> {
  const r = await fetch(`${API_BASE}/drops/active`, { cache: 'no-store' });
  if (!r.ok) throw new Error(`drops/active ${r.status}`);
  return r.json();
}

export async function uploadPhoto(file: File): Promise<{ key: string }> {
  const fd = new FormData();
  fd.append('photo', file);
  const r = await fetch(`${API_BASE}/photos`, { method: 'POST', body: fd });
  if (!r.ok) {
    const data = await r.json().catch(() => ({})) as { error?: string };
    throw new Error(data.error || `photo upload ${r.status}`);
  }
  return r.json();
}

export async function submitClaim(dropId: string, payload: ClaimPayload): Promise<ClaimResponse | ApiError> {
  const r = await fetch(`${API_BASE}/drops/${dropId}/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await r.json() as ClaimResponse | { error: string };
  if (!r.ok) {
    return { ok: false, error: (data as { error: string }).error || `http_${r.status}`, status: r.status };
  }
  return data as ClaimResponse;
}

// ============================================================================
// WebSocket — live updates from DropRoom
// ============================================================================

export function subscribeDrop(dropId: string, onMessage: (msg: unknown) => void): () => void {
  const wsBase = API_BASE.startsWith('http')
    ? API_BASE.replace(/^http/, 'ws')
    : new URL(API_BASE, location.origin).href.replace(/^http/, 'ws');
  const url = `${wsBase}/drops/${dropId}/ws`;

  let ws: WebSocket | null = null;
  let closed = false;
  let retryDelay = 1000;
  let retryTimer: number | null = null;

  const connect = () => {
    if (closed) return;
    try {
      ws = new WebSocket(url);
      ws.addEventListener('open', () => { retryDelay = 1000; });
      ws.addEventListener('message', (ev) => {
        try { onMessage(JSON.parse(ev.data)); } catch { /* ignore */ }
      });
      ws.addEventListener('close', () => {
        if (closed) return;
        retryTimer = window.setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 1.5, 15000);
      });
      ws.addEventListener('error', () => {
        try { ws?.close(); } catch { /* ignore */ }
      });
    } catch {
      retryTimer = window.setTimeout(connect, retryDelay);
    }
  };

  connect();

  return () => {
    closed = true;
    if (retryTimer != null) clearTimeout(retryTimer);
    try { ws?.close(); } catch { /* ignore */ }
  };
}
